import { Injectable } from '@angular/core';
import { isSuffixSegment, isIATCI } from '@common/helpers';
import { JourneyElements } from '@common/interface';
import {
  JourneyElement,
  PostJourneysGetJourneyResponseDataFlightsInner,
  PostJourneysGetJourneyResponseDataTravelersInner,
} from 'src/sdk_checkin';

@Injectable({
  providedIn: 'root',
})
export class CheckinSeatmapDisplaySegmentService {
  constructor() {}

  /**
   * 座席指定可能なチェックインステータスであるかを判定する
   * @param traveler 搭乗者
   * @param journeyElement 当該セグメントのjourneyElement
   * @returns 以下の条件を全て満たす搭乗者である場合true、それ以外の場合false
   * ・旅程を通じたチェックインステータスが、チェックイン不可、未チェックイン、スタンバイチェックイン済みでない
   * ・当該セグメントのチェックインステータスがチェックイン済み(スタンバイチェックインを除く)
   */
  canSelectSeatCheckinStatus(
    traveler: PostJourneysGetJourneyResponseDataTravelersInner,
    journeyElement?: JourneyElement
  ) {
    // 旅程を通じたチェックインステータスが、チェックイン不可、未チェックイン、スタンバイチェックイン済みでないか
    const isSelectSeatAllSegmentCheckinStatus =
      traveler.acceptanceStatusSummary !==
        PostJourneysGetJourneyResponseDataTravelersInner.AcceptanceStatusSummaryEnum.NotAccepted &&
      traveler.acceptanceStatusSummary !==
        PostJourneysGetJourneyResponseDataTravelersInner.AcceptanceStatusSummaryEnum.StandBy &&
      traveler.acceptanceStatusSummary !==
        PostJourneysGetJourneyResponseDataTravelersInner.AcceptanceStatusSummaryEnum.Ineligible;

    // 当該セグメントのチェックインステータスがチェックイン済みであるか
    const isSelectSeatTargetSegmentCheckinStaus =
      journeyElement?.checkInStatus === JourneyElement.CheckInStatusEnum.Accepted;

    return isSelectSeatAllSegmentCheckinStatus && isSelectSeatTargetSegmentCheckinStaus;
  }

  /**
   * 与えられたセグメントが、座席指定対象セグメントかを以下の条件にて判定する
   * ・当該セグメントがSuffix便でない
   * ・提携自社運航便である
   * ・当該セグメントがIATCIでない (notIATCIがtrueの場合のみの条件)
   * NOTE：これはどの画面からの遷移であっても共通的に実施される判定であり、遷移元によってはこの条件に加えて追加の条件で判定する部分もある
   * @param segment 判定対象のセグメント
   * @param notIATCI IATCIのセグメントを含まないかどうか(trueの場合、IATCIを含まない)
   * @returns 条件を全て満たす場合、表示対象セグメントであるとしてtrueを返却し、それ以外の場合は表示対象ではないとしてfalseを返却する
   */
  isDisplayTargetSegment(segment: PostJourneysGetJourneyResponseDataFlightsInner, notIATCI?: boolean) {
    // 提携自社運行便かどうか
    const isDisplayTargetSegument =
      segment.operatingType === PostJourneysGetJourneyResponseDataFlightsInner.OperatingTypeEnum.NhGroupOperating;

    if (notIATCI) {
      return !isSuffixSegment(segment.marketingFlightNumber || '') && !isIATCI(segment) && isDisplayTargetSegument;
    } else {
      return !isSuffixSegment(segment.marketingFlightNumber || '') && isDisplayTargetSegument;
    }
  }

  /**
   * チェックイン状況詳細画面から遷移してきた場合に使用する、表示対象セグメントであるかの判定
   * @param segment 判定対象のセグメント
   * @param travelers 座席指定可能な搭乗者がいるかの判定に使用する搭乗者
   * @param journeyElements 当該セグメントにチェックイン済みであるかを判定するjourneyElement
   * @returns 以下の条件を満たす場合にtrue、それ以外の場合にfalse
   * ・当該セグメントが国際線かつ、NHグループ運行便、もしくは当該セグメントが国内線かつ、NHグループ運行もしくはNHグループ便名である
   * ・当該セグメントがチェックイン受付時間範囲内である
   * ・当該セグメントに対して座席指定可能なチェックインステータスを持つ搭乗者が存在する
   */
  isDisplayTargetSegmentFromCheckinStatus(
    segment: PostJourneysGetJourneyResponseDataFlightsInner,
    travelers: Array<PostJourneysGetJourneyResponseDataTravelersInner>,
    journeyElements: JourneyElements
  ) {
    // 運行キャリア/販売キャリアで表示対象になっているか
    const isDisplayByAirline = this.isDisplayTargetSegment(segment, false);
    // 座席変更時間範囲内で表示対象になっているか
    const isDisplayBySeatChange = segment.isSeatChangeEligible;
    // 搭乗者のチェックインステータスが座席指定可能であるか
    const isDisplatByCheckinStatus = travelers.some((traveler) => {
      const journeyElement = journeyElements[segment.id][traveler.id];
      return this.canSelectSeatCheckinStatus(traveler, journeyElement);
    });

    return isDisplayByAirline && isDisplayBySeatChange && isDisplatByCheckinStatus;
  }
}
